import {Injectable, Injector} from "@angular/core";
import {DxUser} from "../domain/user/dx-user";
import {DxUserSession} from "../domain/user/dx-user-session";
import {DX_AUTH_CONFIG} from "../config/dx-auth-config";
import {DxInMemoryStorageStrategy} from "./strategy/storage/dx-in-memory-storage-strategy";
import {DxAuthStorageStrategyType} from "../config/dx-auth-storage-strategy-type";
import {DxAppError} from "../../dx-core/domain/error/dx-app-error";
import {DxAuthenticationStorageStrategy} from "./strategy/storage/dx-authentication-storage-strategy";
import {DxSessionFactory} from "../domain/user/dx-session-factory";
import {DxLocalStorageStrategy} from "./strategy/storage/dx-local-storage-strategy";
import {DxSessionStorageStrategy} from "./strategy/storage/dx-session-storage-strategy";
import {DxLogger} from "../../dx-core/services/dx-logger.service";
import {DxSessionRefreshStrategy} from "./strategy/session-refresh/dx-session-refresh-strategy";
import {DxKeycloakSessionRefreshStrategy} from "./strategy/session-refresh/dx-keycloak-session-refresh-strategy.service";
import {DxAuthStrategyType} from "../config/dx-auth-stategy-type";
import {DxBasicSessionRefreshStrategy} from "./strategy/session-refresh/dx-basic-session-refresh-strategy.service";
import {Observable} from "rxjs";
import {DxSessionRefreshResult} from "../domain/user/dx-session-refresh-result";
import {map, mergeMap} from "rxjs/operators";
import {DxOperationResultType} from "../../dx-core/domain/common/dx-operation-result-type";
import {DxSessionValidationStrategy} from "./strategy/session-validation/dx-session-validation-strategy";
import {DxBasicValidationStrategy} from "./strategy/session-validation/dx-basic-validation-strategy";
import {DxRefreshTokenValidationStrategy} from "./strategy/session-validation/dx-refresh-token-validation-strategy";
import {DxServerDefinedSessionRefreshStrategy} from "./strategy/session-refresh/dx-server-defined-session-refresh-strategy.service";
import {DxServerDefinedValidationStrategy} from "./strategy/session-validation/dx-server-defined-validation-strategy";
import {DxServerStorageStrategy} from "./strategy/storage/dx-server-storage-strategy";



/** Сервис, предоставляет функциональность для работы с ссесией пользователя на клиенте. */
@Injectable({providedIn: "root"})
export class DxSessionService {

  /** Хранилище для сессии пользователя **/
  private _storage: DxAuthenticationStorageStrategy;
  /** Стратегия работы с сессиями в зависимости от заданного способа аутентификации */
  private _sessionRefreshStrategy: DxSessionRefreshStrategy;
  /** Стратегия валидации сессии. */
  private _sessionValidationStrategy: DxSessionValidationStrategy;


  constructor(private _injector: Injector,
              private _logger: DxLogger) {
    const config = this._injector.get(DX_AUTH_CONFIG);
    if (config) {
      this._storage = DxSessionService._dispatchStorageStrategy(this._injector, config.authStorageStrategyType);
      this._sessionRefreshStrategy = DxSessionService._dispatchSessionRefreshStrategy(this._injector, config.authStrategyType);
      this._sessionValidationStrategy = DxSessionService._dispatchSessionValidationStrategy(this._injector, config.authStrategyType);
    } else {
      this._storage = this._injector.get(DxInMemoryStorageStrategy);
      this._sessionRefreshStrategy = this._injector.get(DxBasicSessionRefreshStrategy);
      this._sessionValidationStrategy = this._injector.get(DxBasicValidationStrategy);
    }
  }

  get storage() {
    return this._storage;
  }

  /** Возвращает текущую сессию пользователя на клиенте. */
  currentSession: DxUserSession;


  /** Создает сессию на основе объекта `DxUser`. */
  public createSession(user: DxUser): DxUserSession {
    const session = DxSessionFactory.createUserSession(user);
    this._storage.store(session);
    return session;
  }


  /** Сносит объект сессии из хранилища */
  public closeSession(): void {
    this._storage.erase();
  }


  /** Проверяет валидность текущей сессии на клиенте. **/
  public isSessionValid(): Observable<boolean> {
    return this._storage.extract().pipe(map((session: DxUserSession) => {
      return this._sessionValidationStrategy.validateSession(session);
    }));
  }


  /** Проверяет необходимость обновления токена. */
  public needRefreshToken(): Observable<boolean> {
    return this._storage.extract().pipe(map( (session: DxUserSession) => {
      if (session.tokenExpTime) this._logger.debug('Token exp. time: ' + new Date(session.tokenExpTime * 1000));
      const currentTime: number = Math.ceil(new Date().getTime() / 1000);
      return session.tokenExpTime && (currentTime > session.tokenExpTime);
    }));
  }


  /** Обновляет сессию на основании рефреш-токена и текушей стратегии обновления. */
  public refreshSession(): Observable<DxSessionRefreshResult> {
      return this._storage.extract().pipe(mergeMap((session: DxUserSession) => {
        return this._sessionRefreshStrategy.refreshSession(session).pipe(map((result: DxSessionRefreshResult) => {
          if(DxOperationResultType.SUCCESS === result.type) {
            this._storage.store(session);
            this._logger.debug('Access token refreshed');
          }
          return result;
        }));
      }));
  }


  private static _dispatchStorageStrategy(injector: Injector, type: DxAuthStorageStrategyType): DxAuthenticationStorageStrategy {
    switch (type) {
      case DxAuthStorageStrategyType.IN_MEMORY:
        return injector.get(DxInMemoryStorageStrategy);
      case DxAuthStorageStrategyType.LOCAL_STORAGE:
        return injector.get(DxLocalStorageStrategy);
      case DxAuthStorageStrategyType.SESSION_STORAGE:
        return injector.get(DxSessionStorageStrategy);
      case DxAuthStorageStrategyType.SERVER_STORAGE:
        return injector.get(DxServerStorageStrategy);
      default:
        const error = new DxAppError();
        error.message = 'Передан неподдерживаемый тип стратегии хранения сессии';
        throw error;
    }
  }

  private static _dispatchSessionRefreshStrategy(injector: Injector, type: DxAuthStrategyType): DxSessionRefreshStrategy {
    switch (type) {
      case DxAuthStrategyType.BASIC_AUTH:
        return injector.get(DxBasicSessionRefreshStrategy);
      case DxAuthStrategyType.JWT_TOKEN_AUTH:
        return injector.get(DxBasicSessionRefreshStrategy);
      case DxAuthStrategyType.DOMAIN_AUTH:
        return injector.get(DxBasicSessionRefreshStrategy);
      case DxAuthStrategyType.OAUTH2:
        return injector.get(DxBasicSessionRefreshStrategy);
      case DxAuthStrategyType.KEY_CLOAK_AUTH:
        return injector.get(DxKeycloakSessionRefreshStrategy);
      case DxAuthStrategyType.SERVER_DEFINED:
        return injector.get(DxServerDefinedSessionRefreshStrategy);
      default:
        const error = new DxAppError();
        error.message = 'Передан неподдерживаемый тип стратегии работы с сессиями!';
        throw error;
    }
  }

  private static _dispatchSessionValidationStrategy(injector: Injector, type: DxAuthStrategyType): DxSessionValidationStrategy {
    switch (type) {
      case DxAuthStrategyType.BASIC_AUTH:
        return injector.get(DxBasicValidationStrategy);
      case DxAuthStrategyType.JWT_TOKEN_AUTH:
        return injector.get(DxBasicValidationStrategy);
      case DxAuthStrategyType.DOMAIN_AUTH:
        return injector.get(DxBasicValidationStrategy);
      case DxAuthStrategyType.OAUTH2:
        return injector.get(DxBasicValidationStrategy);
      case DxAuthStrategyType.KEY_CLOAK_AUTH:
        return injector.get(DxRefreshTokenValidationStrategy);
      case DxAuthStrategyType.SERVER_DEFINED:
        return injector.get(DxServerDefinedValidationStrategy);
      default:
        const error = new DxAppError();
        error.message = 'Передан неподдерживаемый тип стратегии работы с сессиями!';
        throw error;
    }
  }
}
